/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { canPanelManagePremium } from '../../panelAccess/panelRoleAccess';
import { useModerationManageState, type TabKey } from './hooks/useModerationManageState';
import { useModerationManageActions } from './hooks/useModerationManageActions';
import PlayerProfileSection from './components/moderation/PlayerProfileSection';
import BuildingsModerationTab from './components/moderation/BuildingsModerationTab';
import VehiclesModerationTab from './components/moderation/VehiclesModerationTab';
import StaffModerationTab from './components/moderation/StaffModerationTab';
import InterventionsModerationTab from './components/moderation/InterventionsModerationTab';

const TAB_LABELS: Record<TabKey, string> = {
  buildings: 'Bâtiments',
  vehicles: 'Véhicules',
  staff: 'Personnel',
  interventions: 'Interventions',
};

/**
 * Page « Gestion possessions joueur » (panel V1).
 *
 * Orchestrateur fin : tout l'état + tous les handlers sont déplacés dans
 * deux hooks (`useModerationManageState`, `useModerationManageActions`),
 * et chaque section JSX est extraite dans un sous-composant
 * (PlayerProfileSection + 4 *ModerationTab).
 */
const PanelModerationManageV1: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ userId: string }>();
  const userId = Number(params.userId || 0);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const canManagePremium = canPanelManagePremium(currentUser?.roles_id ?? 99);
  const canResetAccount = (currentUser?.roles_id ?? 99) <= 2;
  const canDeleteAccount = (currentUser?.roles_id ?? 99) === 1;

  const state = useModerationManageState({ userId });
  const actions = useModerationManageActions({
    userId,
    canManagePremium,
    tab: state.tab,
    editForm: state.editForm,
    setEditForm: state.setEditForm,
    editingId: state.editingId,
    setEditingId: state.setEditingId,
    setError: state.setError,
    vehicleStatusCodeById: state.vehicleStatusCodeById,
    vehicleStatusCodeByName: state.vehicleStatusCodeByName,
    playerProfile: state.playerProfile,
    setSavingProfile: state.setSavingProfile,
    economyAction: state.economyAction,
    setEconomyAction: state.setEconomyAction,
    setEconomyAdjusting: state.setEconomyAdjusting,
    generatedScenarioId: state.generatedScenarioId,
    load: state.load,
    loadProfile: state.loadProfile,
    loadCtaBuildings: state.loadCtaBuildings,
  });

  const thCls =
    'border-b border-[#1a2537]/80 bg-[#0e1522]/60 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-cockpit-muted';
  const tdCls = 'border-b border-[#1a2537]/60 px-2 py-2 align-middle text-[11px] text-cockpit-text';

  return (
    <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/75 p-4 backdrop-blur-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-cockpit-text">Gestion possessions — joueur #{userId}</h2>
        <button
          type="button"
          onClick={() => navigate('/panel/moderation')}
          className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1.5 text-xs font-medium text-cockpit-text transition-colors hover:bg-cockpit-bg-2/45"
        >
          Retour
        </button>
      </div>

      {state.playerProfile && (
        <PlayerProfileSection
          playerProfile={state.playerProfile}
          setPlayerProfile={state.setPlayerProfile}
          canManagePremium={canManagePremium}
          economyAction={state.economyAction}
          setEconomyAction={state.setEconomyAction}
          economyAdjusting={state.economyAdjusting}
          savingProfile={state.savingProfile}
          adjustEconomy={actions.adjustEconomy}
          savePlayerProfile={actions.savePlayerProfile}
          canResetAccount={canResetAccount}
          canDeleteAccount={canDeleteAccount}
          onDeleted={() => navigate('/panel/moderation')}
        />
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['buildings', 'vehicles', 'staff', 'interventions'] as TabKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              state.setTab(k);
              state.setEditingId(null);
              state.setEditForm({});
            }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              state.tab === k
                ? 'border-[#6366f1]/60 bg-[#6366f1]/80 text-cockpit-text'
                : 'border-[#223046]/70 bg-[#0e1522]/60 text-cockpit-text-2 hover:bg-[#0e1522]/80'
            }`}
          >
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>

      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#223046]/70 bg-[#0e1522]/60 px-2 py-2 backdrop-blur-sm">
        <span className="text-xs text-cockpit-text-2">Recherche ({TAB_LABELS[state.tab]})</span>
        <input
          value={state.searchByTab[state.tab]}
          onChange={(e) => state.setSearchByTab((prev) => ({ ...prev, [state.tab]: e.target.value }))}
          placeholder="Nom, ID, statut, adresse..."
          className="w-full min-w-[220px] rounded-lg border border-[#223046]/90 bg-[#0e1522]/75 px-2 py-1 text-xs text-cockpit-text placeholder:text-cockpit-muted backdrop-blur-sm"
        />
        <span className="text-[11px] text-cockpit-muted">
          {state.filteredItems.length}/{state.items.length}
        </span>
      </div>

      {state.tab === 'interventions' && (
        <div className="mb-3 rounded-xl border border-[#223046]/70 bg-[#0e1522]/60 p-3 text-xs text-cockpit-text backdrop-blur-sm">
          <div className="mb-2 font-semibold text-cockpit-text">
            Générer une intervention (scénario CTA — adresse aléatoire, comme &quot;tester sur mon
            compte&quot;)
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[11px] text-cockpit-text-2">
              <span>Scénario</span>
              <select
                value={state.generatedScenarioId}
                onChange={(e) => state.setGeneratedScenarioId(e.target.value)}
                className="min-w-[280px] rounded-lg border border-[#223046]/90 bg-[#0e1522]/75 px-2 py-1.5 text-xs text-cockpit-text backdrop-blur-sm"
              >
                <option value="">Sélectionner un scénario CTA</option>
                {state.ctaScenarios.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    #{s.id} — {s.scenario_name || 'Scénario'}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void actions.generateFromScenario()}
              className="rounded-lg border border-indigo-500/40 bg-indigo-500/15 px-3 py-1.5 text-xs font-medium text-indigo-100 transition-colors hover:bg-indigo-500/25"
            >
              Générer
            </button>
          </div>
        </div>
      )}

      {state.loading && <p className="text-cockpit-text-2">Chargement...</p>}
      {state.error && <p className="mb-2 text-red-300">{state.error}</p>}

      {!state.loading && state.tab === 'buildings' && (
        <BuildingsModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          toggleBuildingActive={actions.toggleBuildingActive}
          updateBuildingCta={actions.updateBuildingCta}
          buildingCtaLink={state.buildingCtaLink}
          setBuildingCtaLink={state.setBuildingCtaLink}
          ctaBuildings={state.ctaBuildings}
          possessionData={state.possessionData}
        />
      )}

      {!state.loading && state.tab === 'vehicles' && (
        <VehiclesModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          lookup={state.lookup}
          buildingOptions={state.buildingOptions}
        />
      )}

      {!state.loading && state.tab === 'staff' && (
        <StaffModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          lookup={state.lookup}
          buildingOptions={state.buildingOptions}
        />
      )}

      {!state.loading && state.tab === 'interventions' && (
        <InterventionsModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          resetIntervention={actions.resetIntervention}
          cancelIntervention={actions.cancelIntervention}
        />
      )}
    </div>
  );
};

export default PanelModerationManageV1;
